// status-progress-ticket-release-and-worker-readers.ts
//
// Progress panel P1: the small best-effort reads behind readStatusProgress
// (split out to keep status-progress-facts-reader.ts under 200 lines) —
// ticket files with an error CODE instead of a silent [] (N8), the release
// config presence, the live worker pid (B5), and the ledger-confirmed recovery pairs
// (X7/Y2). None of them throws.
import { promises as fs } from "node:fs";
import { hostname } from "node:os";
import { join } from "node:path";
import { readActiveWorker } from "./active-worker-record.js";
import type { JournalRecord } from "./journal-scan-report.js";
import { deadOrPidReused, type LockLivenessProbe } from "./process-liveness-probe.js";
import { recoveryLedgerFromRecords } from "./recovery-ledger-from-journal.js";
import { isRecoveryTicketFor, recoverySourceLabel } from "./recovery-ticket-shape.js";
import type { Ticket } from "./schemas/ticket-schema.js";
import type { JourneyRecovery } from "./status-progress-journey-steps.js";
import { recoveryStatusItems } from "./status-recoveries-from-journal.js";
import { loadTicketsFromRepo } from "./supervisor-ticket-projection.js";

/** `duplicate_ticket_id: …` ⇒ `duplicate_ticket_id`; anything else (schema, JSON) ⇒ `tickets_unreadable`. */
function ticketsErrorCode(err: unknown): string {
  const message = err instanceof Error ? err.message : String(err);
  return /^([a-z_]+):/.exec(message)?.[1] ?? "tickets_unreadable";
}

export async function loadTickets(
  missionWorkspace: string,
): Promise<{ tickets: Ticket[]; error?: string }> {
  try {
    return { tickets: await loadTicketsFromRepo(missionWorkspace) };
  } catch (err) {
    return { tickets: [], error: ticketsErrorCode(err) };
  }
}

/** `config.release` present in the run's effective-config.json snapshot (absent file ⇒ false). */
export async function readReleaseConfigured(controlRoot: string): Promise<boolean> {
  try {
    const parsed = JSON.parse(
      await fs.readFile(join(controlRoot, "effective-config.json"), "utf8"),
    );
    return typeof parsed?.release === "object" && parsed.release !== null;
  } catch {
    return false;
  }
}

/** active-worker.json's pid when the record is ours (same host) and that process still runs. */
export async function readLiveWorkerPid(controlRoot: string, probe: LockLivenessProbe) {
  const record = await readActiveWorker(controlRoot);
  if (record === undefined) return undefined;
  if (record.hostname !== undefined && record.hostname !== hostname()) return undefined;
  return (await deadOrPidReused(probe, record.pid, record.worker_started_at))
    ? undefined
    : record.pid;
}

/**
 * X7/Y2: a recovery ticket is the ledger's APPLIED pair and a ticket file
 * carrying exactly that pair's `sources` label — a `recovery:` label with no
 * ledger op is an ordinary ticket (a worker cannot fake "đang gỡ").
 */
export function confirmedRecoveries(
  records: readonly JournalRecord[],
  ticketStatus: Readonly<Record<string, string>>,
  byId: ReadonlyMap<string, Ticket>,
): JourneyRecovery[] {
  const items = recoveryStatusItems(
    recoveryLedgerFromRecords(records),
    new Map(Object.entries(ticketStatus)),
  );
  return items.flatMap((item) => {
    const id = item.recovery_ticket;
    if (item.result !== "applied" || id === undefined) return [];
    const label = recoverySourceLabel(item.source_ticket, item.trigger);
    if (!isRecoveryTicketFor(byId.get(id), label)) return [];
    return [{ source_ticket: item.source_ticket, trigger: item.trigger, recovery_ticket: id }];
  });
}
